import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { NavBar } from './components/NavBar';
import { Admin } from './pages/Admin';
import { AuthCallback } from './pages/AuthCallback';
import { Calendar } from './pages/Calendar';
import { Home } from './pages/Home';
import { Journal } from './pages/Journal';
import { Leaderboard } from './pages/Leaderboard';
import { Library } from './pages/Library';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { Practice } from './pages/Practice';
import { Practices } from './pages/Practices';
import { Settings } from './pages/Settings';
import { UserProvider, useUser } from './state/UserContext';
import { ThemeProvider } from './theme/ThemeProvider';

function FullPageSpinner() {
  return (
    <div className="center-page">
      <div className="spinner" />
    </div>
  );
}

/** Gates everything behind Cognito auth (or the mock auto-login). */
function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Login />;

  return <Outlet />;
}

/** Wires up the per-user data + theming context once we know the user is authenticated. */
function AppProviders() {
  return (
    <UserProvider>
      <ThemeProvider>
        <Outlet />
      </ThemeProvider>
    </UserProvider>
  );
}

/** Redirects to Onboarding until the user has selected at least one tradition. */
function RequireTraditions() {
  const { me, isLoading } = useUser();

  if (isLoading) return <FullPageSpinner />;
  if (me && me.traditions.length === 0) return <Navigate to="/onboarding" replace />;

  return <Outlet />;
}

function Layout() {
  return (
    <div className="app-shell">
      <NavBar />
      <main className="app-content">
        <Outlet />
      </main>
    </div>
  );
}

function RootRedirect() {
  const { me, isLoading } = useUser();

  if (isLoading) return <FullPageSpinner />;
  if (me && me.traditions.length === 0) return <Navigate to="/onboarding" replace />;

  return <Navigate to="/home" replace />;
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route element={<RequireAuth />}>
            <Route element={<AppProviders />}>
              <Route path="/onboarding" element={<Onboarding />} />

              <Route element={<RequireTraditions />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<Home />} />
                  <Route path="/practices" element={<Practices />} />
                  <Route path="/practice/:id" element={<Practice />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/library" element={<Library />} />
                  <Route path="/journal" element={<Journal />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin" element={<Admin />} />
                </Route>
              </Route>

              <Route path="/" element={<RootRedirect />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
